class ProductBuildInspiration extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-build-track]");
    this.cards = Array.from(this.querySelectorAll("[data-build-card]"));
    this.lightbox = this.querySelector("[data-build-lightbox]");
    this.lightboxPanel = this.querySelector(".product-build-inspiration__lightbox-panel");
    this.lightboxMedia = this.querySelector(".product-build-inspiration__lightbox-media");
    this.lightboxImage = this.querySelector("[data-lightbox-image]");
    this.lightboxVideo = this.querySelector("[data-lightbox-video]");
    this.lightboxBrand = this.querySelector("[data-lightbox-brand]");
    this.lightboxModel = this.querySelector("[data-lightbox-model]");
    this.lightboxProduct = this.querySelector("[data-lightbox-product]");
    this.currentIndex = 0;
    this.currentColumnCount = 0;
    this.touchStartX = null;
    this.touchStartY = null;

    this.onKeydown = (event) => this.handleKeydown(event);
    this.onResize = () => this.handleResize();
    this.onTouchStart = (event) => this.handleTouchStart(event);
    this.onTouchEnd = (event) => this.handleTouchEnd(event);

    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("resize", this.onResize, { passive: true });
    this.rebuildColumns();

    this.cards.forEach((card, index) => {
      card.querySelector("[data-build-open]")?.addEventListener("click", () => this.openLightbox(index));
    });
    this.querySelector("[data-build-close]")?.addEventListener("click", () => this.closeLightbox());
    this.querySelector("[data-lightbox-prev]")?.addEventListener("click", () => this.showOffset(-1));
    this.querySelector("[data-lightbox-next]")?.addEventListener("click", () => this.showOffset(1));
    this.lightbox?.addEventListener("click", (event) => {
      if (event.target === this.lightbox) this.closeLightbox();
    });
    this.lightboxMedia?.addEventListener("touchstart", this.onTouchStart, { passive: true });
    this.lightboxMedia?.addEventListener("touchend", this.onTouchEnd, { passive: true });

    this.setupHoverVideos();

  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("resize", this.onResize);
    this.lightboxMedia?.removeEventListener("touchstart", this.onTouchStart);
    this.lightboxMedia?.removeEventListener("touchend", this.onTouchEnd);
    window.cancelAnimationFrame(this.resizeFrame);
    document.documentElement.classList.remove("product-build-inspiration-open");
    document.body?.classList.remove("product-build-inspiration-open");
  }

  configuredColumnCount(propertyName, fallback) {
    const value = Number.parseInt(getComputedStyle(this).getPropertyValue(propertyName), 10);
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }

  columnCount() {
    const width = window.innerWidth;
    if (width <= 699) return this.configuredColumnCount("--pbi-columns-mobile", 2);
    if (width <= 899) return 3;
    if (width <= 1199) return 4;
    return this.configuredColumnCount("--pbi-columns-desktop", 4);
  }

  handleResize() {
    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      if (this.columnCount() !== this.currentColumnCount) this.rebuildColumns();
    });
  }

  rebuildColumns() {
    if (!this.track || !this.cards.length) return;

    const columnCount = this.columnCount();
    const columns = [];
    this.track.replaceChildren();

    for (let index = 0; index < columnCount; index += 1) {
      const column = document.createElement("div");
      column.className = "product-build-inspiration__column";
      columns.push(column);
      this.track.append(column);
    }

    this.cards.forEach((card, index) => columns[index % columnCount].append(card));
    this.track.classList.add("is-masonry");
    this.currentColumnCount = columnCount;
  }

  setupHoverVideos() {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.cards.forEach((card) => {
      const video = card.querySelector("[data-build-hover-video]");
      if (!video) return;

      card.addEventListener("mouseenter", () => this.playHoverVideo(card, video));
      card.addEventListener("mouseleave", () => this.stopHoverVideo(card, video));
      card.addEventListener("focusin", () => this.playHoverVideo(card, video));
      card.addEventListener("focusout", (event) => {
        if (!card.contains(event.relatedTarget)) this.stopHoverVideo(card, video);
      });
    });
  }

  playHoverVideo(card, video) {
    video.dataset.hoverActive = "true";
    video.muted = true;

    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      video.load();
    }

    video.play().then(() => {
      if (video.dataset.hoverActive === "true") card.classList.add("is-video-playing");
    }).catch(() => {});
  }

  stopHoverVideo(card, video) {
    video.dataset.hoverActive = "false";
    video.pause();
    video.currentTime = 0;
    card.classList.remove("is-video-playing");
  }

  stopAllHoverVideos() {
    this.cards.forEach((card) => {
      const video = card.querySelector("[data-build-hover-video]");
      if (video) this.stopHoverVideo(card, video);
    });
  }

  openLightbox(index) {
    if (!this.lightbox || !this.cards[index]) return;
    this.stopAllHoverVideos();
    this.currentIndex = index;
    this.updateLightbox();
    this.lightbox.hidden = false;
    document.documentElement.classList.add("product-build-inspiration-open");
    document.body?.classList.add("product-build-inspiration-open");
    this.querySelector("[data-build-close]")?.focus();
  }

  closeLightbox() {
    if (!this.lightbox || this.lightbox.hidden) return;
    this.lightbox.hidden = true;
    this.resetMedia();
    document.documentElement.classList.remove("product-build-inspiration-open");
    document.body?.classList.remove("product-build-inspiration-open");
    this.cards[this.currentIndex]?.querySelector("[data-build-open]")?.focus();
  }

  showOffset(offset) {
    if (!this.cards.length) return;
    this.currentIndex = (this.currentIndex + offset + this.cards.length) % this.cards.length;
    this.updateLightbox();
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1) {
      this.touchStartX = null;
      this.touchStartY = null;
      return;
    }

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  handleTouchEnd(event) {
    if (this.touchStartX === null || this.touchStartY === null || event.changedTouches.length !== 1) return;

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const deltaY = event.changedTouches[0].clientY - this.touchStartY;
    this.touchStartX = null;
    this.touchStartY = null;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
    this.showOffset(deltaX < 0 ? 1 : -1);
  }

  updateLightbox() {
    const card = this.cards[this.currentIndex];
    const media = card?.querySelector("[data-build-media]");
    if (!card || !media) return;

    this.resetMedia();
    this.setLightboxAspect(media);
    if (media.dataset.type === "video") {
      this.lightboxVideo.src = media.dataset.src || "";
      if (media.dataset.poster) this.lightboxVideo.poster = media.dataset.poster;
      this.lightboxVideo.hidden = false;
      this.lightboxVideo.play().catch(() => {});
    } else {
      this.lightboxImage.src = media.dataset.src || "";
      this.lightboxImage.alt = media.dataset.alt || "";
      this.lightboxImage.hidden = false;
    }

    if (this.lightboxBrand) {
      this.lightboxBrand.textContent = card.querySelector(".product-build-inspiration__brand")?.textContent?.trim() || "";
    }
    if (this.lightboxModel) {
      this.lightboxModel.textContent = card.querySelector(".product-build-inspiration__model")?.textContent?.trim() || "";
    }
    if (this.lightboxProduct) {
      this.lightboxProduct.textContent = card.querySelector(".product-build-inspiration__product")?.textContent?.trim() || "";
    }

    const hasMultiple = this.cards.length > 1;
    this.querySelector("[data-lightbox-prev]").hidden = !hasMultiple;
    this.querySelector("[data-lightbox-next]").hidden = !hasMultiple;
  }

  setLightboxAspect(media) {
    if (!this.lightboxPanel) return;
    const width = Number(media.dataset.width);
    const height = Number(media.dataset.height);
    const aspect = width > 0 && height > 0 ? width / height : 1.5;
    this.lightboxPanel.style.setProperty("--pbi-lightbox-aspect", String(aspect));
  }

  resetMedia() {
    if (this.lightboxVideo) {
      this.lightboxVideo.pause();
      this.lightboxVideo.removeAttribute("src");
      this.lightboxVideo.removeAttribute("poster");
      this.lightboxVideo.hidden = true;
    }
    if (this.lightboxImage) {
      this.lightboxImage.removeAttribute("src");
      this.lightboxImage.alt = "";
      this.lightboxImage.hidden = true;
    }
  }

  handleKeydown(event) {
    if (!this.lightbox || this.lightbox.hidden) return;
    if (event.key === "Escape") this.closeLightbox();
    if (event.key === "ArrowLeft") this.showOffset(-1);
    if (event.key === "ArrowRight") this.showOffset(1);
  }
}

if (!customElements.get("product-build-inspiration")) {
  customElements.define("product-build-inspiration", ProductBuildInspiration);
}
